import React, { useContext, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { IApp03Epic, IApp03Portfolio, IApp03Project, IApp03Sprint, IApp03Task, IApp03Template, IApp03Workflow, IApp04AIChat, IEntity, IEntityDescription1, IProject, ListDefaults } from '../../types';
import { EntityList } from '../EntityList';
import { ErrorBoundary } from '../ErrorBoundary';
import { IWithErrorHandlingProps, withErrorHandling } from '../withErrorHandling';
import { IUseApiProps } from '../useApi';
import { renderActiveTasks, renderAsLink, renderDateRange, renderDateRangeSimple, renderPortfolioIcons, renderTaskPriority, renderWithBackgroundColor, renderWorkflowIcon } from '../cr/ListRenderers';
import { DataContext } from '../DataContext';
import useMobileDetector from '../useMobileDetector';
import { TaskPriority, TaskPriorityColors, WorkflowType, getTaskPriorityString, getWorkflowTypeString } from '../../refData/WorkflowType';
import { WorkflowForm } from './WorkflowForm';
import { UserWorkflowForm } from './UserWorkflowForm';
import { OrbUserContext } from '../OrbUserContext';
import { WorkflowView } from './WorkflowView';
import { WFPortfolioForm } from './WFPortfolioForm';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { PortfolioTypes } from '../../types/App03Portfolio';
import { MessageDialog } from '../cr/MessageDialog';
import { AppIDs, getAppName } from '../../AppGlobals';
import { App03Frameworks, App03WorkflowParentTypes } from '../../types/App03Workflow';
import { SprintFormPage } from './SprintFormPage';
import { App03SprintStatus } from '../../types/App03Sprint';
import { CloseSprintForm } from './CloseSprintForm';
import { EpicFormPage } from './EpicFormPage';
import { getApp03EpicStatusDisplay } from '../../types/App03Epic';
import { DateService } from '../../services';
import { WFProjectForm } from './WFProjectForm';
import { unwatchFile } from 'fs';
import { App03TemplateForm } from './App03TemplateForm';
import { App03WorkItemTypeForm } from './App03WorkItemTypeForm';
import { app03EpicColumns, app03EpicTypes, ganttHighlightTime, GanttZoomLevel, svarGanttZoomConfig } from '../../svargantt/svarGanttConfig';
import { IUpdateEntityDatesPayload } from '../../services/EntityService';
import { decodeRouteId, encodeRouteId } from '../../utils/routeId';
import { AiChatForm } from '../AI/AiChatForm';

export interface IApp03WorkflowsAdminProps extends IUseApiProps, IWithErrorHandlingProps {
    frameworkID: App03Frameworks;
    entity: string;

    lastTemplateIdForChilds?: number | null;
    setLastTemplateIdForChilds?: React.Dispatch<React.SetStateAction<number | null>>;

    lastPortfolioIdForChilds?: number | null;
    setLastPortfolioIdForChilds?: React.Dispatch<React.SetStateAction<number | null>>;

    lastProjectIdForChilds?: number | null;
    setLastProjectIdForChilds?: React.Dispatch<React.SetStateAction<number | null>>;
    menuData?: any[];
    onEntityDescriptionChange: (desc: IEntityDescription1) => void;
}


const App03WorkflowsAdmin = (props: IApp03WorkflowsAdminProps) => {
    const { entity } = props;
    const { dataServices } = useContext(DataContext);;
    const { userContext } = useContext(OrbUserContext);
    const isMobile = useMobileDetector();
    const history = useHistory();
    const [workflowsLabelForSelectedPortfolio, setWorkflowsLabelForSelectedPortfolio] = useState<string>("Workstreams");
    const [workflowsLabelForSelectedProject, setWorkflowsLabelForSelectedProject] = useState<string>("Workstreams");
    const [projectsLabelForSelectedPortfolio, setProjectsLabelForSelectedPortfolio] = useState<string>("Projects");
    const [workItemTypesLabelForSelectedTemplate, setWorkItemTypesLabelForSelectedTemplate] = useState<string>("Work Item Types");
    const [tasksForCounters, setTasksForCounters] = useState<IApp03Task[]>([]);
    const [epicsForCounters, setEpicsForCounters] = useState<IApp03Epic[]>([]);
    const [workflowsForCounters, setWorkflowsForCounters] = useState<IApp03Workflow[]>([]);
    const { portfolioIdForProjects: portfolioIdForProjects_encoded } = useParams<{ portfolioIdForProjects: string }>();
    const portfolioIdForProjects = decodeRouteId(portfolioIdForProjects_encoded) ?? portfolioIdForProjects_encoded;

    const { portfolioIdForWorkflows: portfolioIdForWorkflows_encoded } = useParams<{ portfolioIdForWorkflows: string }>();
    const portfolioIdForWorkflows = decodeRouteId(portfolioIdForWorkflows_encoded) ?? portfolioIdForWorkflows_encoded;

    const { projectIdForWorkflows: projectIdForWorkflows_encoded } = useParams<{ projectIdForWorkflows: string }>();
    const projectIdForWorkflows = decodeRouteId(projectIdForWorkflows_encoded) ?? projectIdForWorkflows_encoded;

    const { workflowIdForWorkflowUsers: workflowIdForWorkflowUsers_encoded } = useParams<{ workflowIdForWorkflowUsers: string }>();
    const workflowIdForWorkflowUsers = decodeRouteId(workflowIdForWorkflowUsers_encoded) ?? workflowIdForWorkflowUsers_encoded;

    const { templateIDForWorkItemTypes: templateIDForWorkItemTypes_encoded } = useParams<{ templateIDForWorkItemTypes: string }>();
    const templateIDForWorkItemTypes = decodeRouteId(templateIDForWorkItemTypes_encoded) ?? templateIDForWorkItemTypes_encoded;

    const { workflowIdForSprints: workflowIdForSprints_encoded } = useParams<{ workflowIdForSprints: string }>();
    const workflowIdForSprints = decodeRouteId(workflowIdForSprints_encoded) ?? workflowIdForSprints_encoded;

    const { workflowIdForEpics: workflowIdForEpics_encoded } = useParams<{ workflowIdForEpics: string }>();
    const workflowIdForEpics = decodeRouteId(workflowIdForEpics_encoded) ?? workflowIdForEpics_encoded;
    const workflowNameWidths = isMobile ? { minWidth: 90, maxWidth: 150 } : { minWidth: 150, maxWidth: 250 };

    const getAppUrl = (): string => {
        if (props.frameworkID === App03Frameworks.Scrum) {
            return "/a10";
        }
        else {
            return "/a1";
        }
    }

    const getAppId = (): number => {
        if (props.frameworkID === App03Frameworks.Scrum) {
            return AppIDs.AGSPR;
        }
        else {
            return AppIDs.CORWF;
        }
    }

    const fetchChildCountsForSelectedTemplate = (templateID: number): void => {
        if (templateID) {
            //call services to get total counts
            dataServices.app03WorkItemTypeService.countWorkItemTypesForTemplate(templateID).then(c => {
                setWorkItemTypesLabelForSelectedTemplate(`Work Item Types (${c})`);
            });
        }
    }

    const fetchChildCountsForSelectedPortfolio = (portfolioID: number): void => {
        if (portfolioID) {
            //call services to get total counts
            dataServices.app03ProjectService.countProjectsForPortfolio(portfolioID).then(c => {
                setProjectsLabelForSelectedPortfolio(`Projects (${c})`);
            });

            dataServices.app03WorkflowService.countWorkflowsForPortfolio(portfolioID).then(c => {
                setWorkflowsLabelForSelectedPortfolio(`Workstreams (${c})`);
            });
        }
    }

    const fetchChildCountsForSelectedProject = (projectID: number): void => {
        if (projectID) {
            //call services to get total counts
            dataServices.app03WorkflowService.countWorkflowsForProject(projectID).then(c => {
                setWorkflowsLabelForSelectedProject(`Workstreams (${c})`);
            });
        }
    }

    const getPreFarItems = (keys: string[]) => {
        const config: Record<string, ICommandBarItemProps> = {
            Portfolios: {
                key: 'Portfolios',
                name: 'Back to Portfolios',
                iconProps: { iconName: 'Back' },
                onClick() {
                    props.setLastPortfolioIdForChilds(null);
                    props.setLastProjectIdForChilds(null);
                    history.push(`${getAppUrl()}/cwf-portfolios`);
                },
            },
            Projects: {
                key: 'Projects',
                name: 'Back to Projects',
                iconProps: { iconName: 'Back' },
                onClick() {
                    props.setLastProjectIdForChilds(null);

                    if (props.lastPortfolioIdForChilds != null) {
                        history.push(`${getAppUrl()}/cwf-projects-for-portfolio/${encodeRouteId(props.lastPortfolioIdForChilds!)}`);
                    }
                    else {
                        history.push(`${getAppUrl()}/cwf-projects`);
                    }


                },
            },
            Workflows: {
                key: 'Workflows',
                name: 'Back to Workstreams',
                iconProps: { iconName: 'Back' },
                onClick() {
                    if (props.lastProjectIdForChilds != null) {
                        history.push(`${getAppUrl()}/workflows-for-project/${encodeRouteId(props.lastProjectIdForChilds!)}`);
                    }
                    else if (props.lastPortfolioIdForChilds != null) {
                        history.push(`${getAppUrl()}/workflows-for-portfolio/${encodeRouteId(props.lastPortfolioIdForChilds!)}`);
                    }
                    else {
                        history.push(`${getAppUrl()}/workflows`);
                    }
                },
            },

            Templates: {
                key: 'Templates',
                name: 'Back to Templates',
                iconProps: { iconName: 'Back' },
                onClick() {
                    props.setLastTemplateIdForChilds(null);
                    history.push(`${getAppUrl()}/settings/templates`);
                },
            },

            // Add more configurations as needed for other keys
        };

        const arr: ICommandBarItemProps[] = keys
            .filter((key) => config[key]) // Only include valid keys
            .map((key) => config[key]);

        return arr;
    };

    const openTasks = (workflowID?: number) => {
        history.push(`${getAppUrl()}/workflow-tasks/${encodeRouteId(workflowID!)}`);
    };

    const openSprints = (workflowID?: number) => {
        history.push(`${getAppUrl()}/sprints/${encodeRouteId(workflowID!)}`);
    };

    const openEpics = (workflowID?: number) => {
        history.push(`${getAppUrl()}/epics/${encodeRouteId(workflowID!)}`);
    };

    const openProjectsForPortfolio = (portfolioID?: number) => {
        props.setLastPortfolioIdForChilds(portfolioID);
        history.push(`${getAppUrl()}/cwf-projects-for-portfolio/${encodeRouteId(portfolioID!)}`);
    };

    const openWorkflowsForPortfolio = (portfolioID?: number) => {
        props.setLastPortfolioIdForChilds(portfolioID);
        history.push(`${getAppUrl()}/workflows-for-portfolio/${encodeRouteId(portfolioID!)}`);
    };

    const openWorkflowsForProject = (projectID?: number) => {
        props.setLastProjectIdForChilds(projectID);
        history.push(`${getAppUrl()}/workflows-for-project/${encodeRouteId(projectID!)}`);
    };

    const openWorkItemTypesForTemplate = (templateID?: number) => {
        props.setLastTemplateIdForChilds(templateID);
        history.push(`${getAppUrl()}/settings/work-item-types/${encodeRouteId(templateID!)}`);
    };

    const projectPortfoliosAppName = getAppName(props.menuData, AppIDs.PROJPORT);

    const sprintsEntityListRef = React.useRef<EntityList<IApp03Sprint>>(null);
    const epicsEntityListRef = React.useRef<EntityList<IApp03Epic>>(null);

    const renderEpicPopup = (epic: IApp03Epic) => {
        const parsedStartDate =
            epic.StartDate instanceof Date
                ? epic.StartDate
                : typeof epic.StartDate === 'string'
                    ? new Date(epic.StartDate)
                    : null;

        const parsedEndDate =
            epic.TargetEndDate instanceof Date
                ? epic.TargetEndDate
                : typeof epic.TargetEndDate === 'string'
                    ? new Date(epic.TargetEndDate)
                    : null;

        const startDateStr: string = parsedStartDate
            ? DateService.dateToUkShortDate(parsedStartDate)
            : '';

        const endDateStr: string = parsedEndDate
            ? DateService.dateToUkShortDate(parsedEndDate)
            : '';

        return `
    <div style="font-size:13px; color:#444; line-height:1.6;">
        <div><strong>Status:</strong> ${getApp03EpicStatusDisplay(epic.EpicStatus)}</div>
        <div><strong>Priority:</strong> ${getTaskPriorityString(epic.EpicPriorityID)}</div>
        <div><strong>Start Date:</strong> ${startDateStr}</div>
        <div><strong>End Date:</strong> ${endDateStr}</div>
        <div><strong>Owner:</strong> ${epic.EpicOwner?.Title ?? ''}</div>
    </div>
    `;
    };


    const renderGanttTooltip_epic = ({ data }) => {
        if (!data) return null;

        const startStr = data.start
            ? DateService.dateToUkShortDate(data.start)
            : '';

        const endStr = data.end
            ? DateService.dateToUkShortDate(data.end)
            : '';

        return (
            <div
                style={{
                    whiteSpace: "nowrap",
                    padding: "6px 10px",
                    background: "var(--wx-tooltip-background)",
                    fontSize: "13px",
                    color: "rgba(255,255,255,0.92)"
                }}
            >
                <div style={{ marginBottom: 6 }}>
                    <strong>Epic:</strong> {data.text}
                </div>

                <div>
                    <strong>Start:</strong> {startStr}
                </div>

                {data.end && (
                    <div>
                        <strong>Target End:</strong> {endStr}
                    </div>
                )}
            </div>
        );
    };


    const prevEpicsRef = React.useRef<IApp03Epic[]>();

    const buildEpicGanttData = React.useCallback((epics) => {
        if (prevEpicsRef.current !== epics) {
            console.log("Building Gantt data for epics");
            prevEpicsRef.current = epics;
        }

        const ganttTasks: any[] = [];
        const ganttLinks: any[] = [];

        epics.forEach(e => {
            if (!e.StartDate || !e.TargetEndDate) return;

            const start = new Date(e.StartDate);
            const end = new Date(e.TargetEndDate);

            ganttTasks.push({
                id: `epic-${e.ID}`,
                text: e.Title,
                start,
                end,
                type: "epic",
                _entity: e
            });
        });

        let linkId = 1;
        epics.forEach(e => {
            if (e.Dependencies && e.Dependencies.length > 0) {
                e.Dependencies.forEach((dep: any) => {
                    ganttLinks.push({
                        id: linkId++,
                        source: `epic-${dep.DependentEpicID}`,
                        target: `epic-${e.ID}`,
                        type: "e2s"
                    });
                });
            }
        });

        return { tasks: ganttTasks, links: ganttLinks };
    }, []);

    const openAIChat = (chatID?: number) => {
        const SALT = "zedxcloe";
        const encoded = btoa(`${SALT}_${chatID}_${SALT}`);
        const returnTo = encodeURIComponent(`${getAppUrl()}/ai-chats`);

        history.push(`${getAppUrl()}/ask-cloe/${encoded}?returnTo=${returnTo}`);
    };

    return (
        <ErrorBoundary>

            {entity === 'AIChats' &&
                <EntityList<IApp04AIChat>
                    {...props}
                    disableAdd={false}
                    disableDelete={() => true}
                    entityName={{ Plural: 'Cloe AI Chats', Singular: 'Cloe AI Chat' }}
                    entity={"Cloe AI Chats"} //just to reload
                    columns={[
                        { key: '1', name: 'Chat Title', fieldName: 'Name', ...workflowNameWidths, isMultiline: true, isResizable: true, onRender: item => renderAsLink(item.key, item.Name, (ID: number) => openAIChat(ID)) },
                        { key: '2', name: 'Last Prompt', fieldName: 'LastPrompt', minWidth: 150, maxWidth: 250, isResizable: true, isMultiline: true, },
                        { key: '3', name: 'Prompts', fieldName: 'TotalPrompts', minWidth: 60, maxWidth: 70, isResizable: true, isCollapsible: true },
                        { key: '4', name: 'Owners', fieldName: 'Owners', minWidth: 150, maxWidth: 200, isResizable: true, isCollapsible: true, isMultiline: true },
                        { key: '5', name: 'Dates', minWidth: 100, isResizable: true, isCollapsible: true, onRender: item => renderDateRangeSimple(item.CreatedOn, item.LastPromptAt) },
                    ]}
                    loadListItems={showClosedEntities => {
                        return dataServices.app04AIChatService.readAllForList(showClosedEntities, undefined, getAppId());
                    }}
                    mapEntitiesToListItems={chats => chats.map(c => {
                        return {
                            key: c.ID,
                            Name: c.Title,
                            LastPrompt: c.LastPrompt,
                            TotalPrompts: c.TotalPrompts,
                            Owners: [
                                c.App04AIChatUsers && c.App04AIChatUsers.some(cu => cu.User != null) ? `${c.App04AIChatUsers.map(pu => pu.User?.Title).join(', ')}` : null,
                            ].filter(Boolean).join(', '),
                            CreatedOn: c.CreatedOn,
                            CreatedOnValue: c.CreatedOn?.valueOf(),
                            LastPromptAt: c.LastPromptAt,
                            LastPromptAtValue: c.LastPromptAt?.valueOf(),
                        };
                    })}
                    entityForm={(showForm, entityId, onSaved, onCancelled) =>
                        <AiChatForm
                            showForm={showForm}
                            entityId={entityId}
                            defaultValues={[
                                { field: 'AppID', value: Number(getAppId()) },
                                { field: 'SummaryFrequency', value: 10 },
                            ]}
                            onSaved={onSaved}
                            onCancelled={onCancelled}
                            {...props}
                        />
                    }
                    onCheckDelete={id => dataServices.app03PortfolioService.entityChildren(id)}
                    onDelete={id => dataServices.app03PortfolioService.delete(id)}
                />
            }
            {entity === 'App03Portfolios' &&
                <EntityList<IApp03Portfolio>
                    {...props}
                    disableAdd={false}
                    //enableShowHideClosedEntities={true}
                    entityName={{ Plural: 'Portfolios', Singular: 'Portfolio' }}
                    //barColorBeforeTitle='#40A182'
                    additionalItemsOnSelection={(p) => {
                        fetchChildCountsForSelectedPortfolio(p?.ID);
                        return ([
                            ...(props.frameworkID === App03Frameworks.Scrum
                                ? [{
                                    key: 'Projects',
                                    name: projectsLabelForSelectedPortfolio,
                                    iconProps: { iconName: 'MiniExpand' },
                                    onClick(ev, item) {
                                        openProjectsForPortfolio(p?.ID);
                                    },
                                }]
                                : []),

                            {
                                key: 'Workflows',
                                name: workflowsLabelForSelectedPortfolio,
                                iconProps: { iconName: 'MiniExpand' },
                                onClick(ev, item) {
                                    //props.setLastPortfolioIdForChilds(p?.ID);
                                    openWorkflowsForPortfolio(p?.ID);
                                },
                            },
                        ]);
                    }}
                    columns={[
                        //{ key: '1', name: 'Portfolio', fieldName: 'Name', ...workflowNameWidths, isResizable: true, onRender: item => renderAsLink(item.key, item.Name, (ID: number) => openWorkflowsForPortfolio(ID)) },
                        { key: '1', name: 'Portfolio', fieldName: 'Name', ...workflowNameWidths, isResizable: true, isMultiline: true, },
                        { key: '2', name: 'Type', fieldName: 'PortfolioType', minWidth: 70, maxWidth: 90, isCollapsible: true, isResizable: true },
                        { key: '3', name: 'Workstreams', fieldName: 'Workflows', minWidth: 80, maxWidth: 100, isCollapsible: true, isResizable: true },
                        { key: '4', name: 'Active Tasks', fieldName: 'ActiveTasks', minWidth: 80, maxWidth: 100, isCollapsible: true, isResizable: true },
                        { key: '5', name: 'Critical', fieldName: 'CriticalTasks', minWidth: 60, maxWidth: 60, isCollapsible: true, isResizable: true, onRender: item => renderWithBackgroundColor(item.CriticalTasks, 'red', 'white') },
                        { key: '6', name: 'Owners', fieldName: 'Owners', minWidth: 150, maxWidth: 200, isResizable: true, isCollapsible: true, isMultiline: true },
                    ]}

                    loadListItems={async showClosedEntities => {
                        const tasks: IApp03Task[] = await dataServices.app03TaskService.readAllActiveTasksForCounters(props.frameworkID);
                        const workflows: IApp03Workflow[] = await dataServices.app03WorkflowService.readAllWorkflowsForCounters(showClosedEntities);
                        setTasksForCounters(tasks);
                        setWorkflowsForCounters(workflows);

                        return dataServices.app03PortfolioService.readAllForList(showClosedEntities, props.frameworkID);
                    }}
                    mapEntitiesToListItems={portfolios => portfolios.map(p => {

                        const totalWorkflows: number = workflowsForCounters?.filter(wf => wf.App03PortfolioID === p.ID)?.length ?? 0;
                        const totalUserWorkflows: number = workflowsForCounters?.filter(wf => wf.App03PortfolioID === p.ID && wf.App03WorkflowUsers.some(user => user.UserID === userContext.UserId))?.length ?? 0;

                        const totalTasks: number = tasksForCounters?.filter(t => t.Workflow?.App03PortfolioID === p.ID)?.length ?? 0;
                        const totalUserTasks: number = tasksForCounters?.filter(t => t.Workflow?.App03PortfolioID === p.ID && t.App03TaskUsers.some(user => user.UserID === userContext.UserId))?.length ?? 0;
                        const totalCriticalTasks: number = tasksForCounters?.filter(t => t.Workflow?.App03PortfolioID === p.ID && t.TaskPriorityID === TaskPriority.Critical)?.length ?? 0;
                        const totalUserCriticalTasks: number = tasksForCounters?.filter(t => t.Workflow?.App03PortfolioID === p.ID && t.TaskPriorityID === TaskPriority.Critical && t.App03TaskUsers.some(user => user.UserID === userContext.UserId))?.length ?? 0;

                        return {
                            key: p.ID,
                            Name: p.Title,
                            PortfolioType: p.PortfolioTypeID === PortfolioTypes.General ? "General" : "Project",
                            Workflows: `${totalUserWorkflows}/${totalWorkflows}`, // '2/10',
                            ActiveTasks: `${totalUserTasks}/${totalTasks}`, // '2/10',
                            CriticalTasks: `${totalUserCriticalTasks}/${totalCriticalTasks}`,
                            Owners: [
                                p.App03PortfolioUsers && p.App03PortfolioUsers.some(pu => pu.User != null) ? `${p.App03PortfolioUsers.map(pu => pu.User?.Title).join(', ')}` : null,
                            ].filter(Boolean).join(', '),
                        };
                    })}
                    entityForm={(showForm, entityId, onSaved, onCancelled, entityDescription1, entityDescription1DirectorateID, selectedEntity) => {
                        if (entityId > 0 && selectedEntity?.PortfolioTypeID === PortfolioTypes.Project) {
                            return (
                                <MessageDialog
                                    hidden={!showForm}
                                    title={`${selectedEntity?.Title} cannot be edited`}
                                    handleOk={onCancelled}
                                >
                                    <>
                                        <p>
                                            This portfolio was created in the <strong>{projectPortfoliosAppName}</strong> app.
                                            To make changes, please open the <strong>{projectPortfoliosAppName}</strong> app
                                            and edit it from there.
                                        </p>

                                    </>

                                </MessageDialog>
                            );
                        } else {
                            return (
                                <WFPortfolioForm
                                    {...props}
                                    showForm={showForm}
                                    entityId={entityId}
                                    onSaved={onSaved}
                                    frameworkID={props.frameworkID}
                                    onCancelled={onCancelled}
                                />
                            );
                        }
                    }}

                    onCheckDeleteCustomMsg={(entityId, selectedEntity) => {
                        if (entityId > 0 && selectedEntity?.PortfolioTypeID === PortfolioTypes.Project) {
                            return (
                                <>
                                    <p>
                                        This portfolio was created in the <strong>{projectPortfoliosAppName}</strong> app.
                                        To delete it, please open the <strong>{projectPortfoliosAppName}</strong> app
                                        and delete it from there.
                                    </p>

                                </>
                            );
                        } else {
                            return null;
                        }
                    }}

                    onCheckDelete={id => dataServices.app03PortfolioService.entityChildren(id)}
                    onDelete={id => dataServices.app03PortfolioService.delete(id)}
                />
            }

            {entity === 'App03Projects' &&
                <EntityList<IApp03Project>
                    {...props}
                    disableAdd={false}
                    entity={portfolioIdForProjects !== undefined ? "Projects for portfolio" : "Projects"} //just to reload
                    showEntityDescription1={portfolioIdForProjects !== undefined ? true : false}
                    loadEntityDescription1={() => {
                        return portfolioIdForProjects !== undefined ? dataServices.app03PortfolioService.readTitleForPortfolio('Projects', 'Project', Number(portfolioIdForProjects)) : null;
                    }}
                    onEntityDescriptionChange={props.onEntityDescriptionChange}
                    veryFirstItems={(p) => portfolioIdForProjects !== undefined ? getPreFarItems(['Portfolios']) : null}
                    entityName={{ Plural: 'Projects', Singular: 'Project' }}
                    additionalItemsOnSelection={(p) => {
                        fetchChildCountsForSelectedProject(p?.ID);
                        return ([
                            {
                                key: 'Workflows',
                                name: workflowsLabelForSelectedProject,
                                iconProps: { iconName: 'MiniExpand' },
                                onClick(ev, item) {
                                    openWorkflowsForProject(p?.ID);
                                },
                            },
                        ]);
                    }}
                    columns={[
                        { key: '1', name: 'Project', fieldName: 'Name', ...workflowNameWidths, isResizable: true, isMultiline: true, onRender: item => renderAsLink(item.key, item.Name, (ID: number) => openWorkflowsForProject(ID)) },
                        ...(portfolioIdForProjects === undefined
                            ? [{ key: '2', name: 'Portfolio', fieldName: 'Portfolio', ...workflowNameWidths, isResizable: true, isMultiline: true }]
                            : []),
                        { key: '3', name: 'Type', fieldName: 'App03ProjectType', minWidth: 70, maxWidth: 90, isCollapsible: true, isResizable: true },
                        { key: '4', name: 'Workstreams', fieldName: 'Workflows', minWidth: 80, maxWidth: 100, isCollapsible: true, isResizable: true },
                        { key: '5', name: 'Owner', fieldName: 'Owner', minWidth: 80, maxWidth: 120, isResizable: true, isCollapsible: true, isMultiline: true },

                    ]}

                    loadListItems={async showClosedEntities => {
                        if (portfolioIdForProjects !== undefined) {
                        } else {
                            props.setLastPortfolioIdForChilds(null);
                        }

                        //const tasks: IApp03Task[] = await dataServices.app03TaskService.readAllActiveTasksForCounters(props.frameworkID);
                        const workflows: IApp03Workflow[] = await dataServices.app03WorkflowService.readAllWorkflowsForCounters(showClosedEntities);
                        //setTasksForCounters(tasks);
                        setWorkflowsForCounters(workflows);

                        const projects = portfolioIdForProjects !== undefined
                            ? await dataServices.app03ProjectService.readAllForList(showClosedEntities, props.frameworkID, Number(portfolioIdForProjects))
                            : await dataServices.app03ProjectService.readAllForList(showClosedEntities, props.frameworkID);

                        return projects;
                    }}
                    mapEntitiesToListItems={portfolios => portfolios.map(p => {

                        const totalWorkflows: number = workflowsForCounters?.filter(wf => wf.App03ProjectID === p.ID)?.length ?? 0;
                        //const totalUserWorkflows: number = workflowsForCounters?.filter(wf => wf.App03PortfolioID === p.ID && wf.App03WorkflowUsers.some(user => user.UserID === userContext.UserId))?.length ?? 0;

                        //const totalTasks: number = tasksForCounters?.filter(t => t.Workflow?.App03PortfolioID === p.ID)?.length ?? 0;
                        //const totalUserTasks: number = tasksForCounters?.filter(t => t.Workflow?.App03PortfolioID === p.ID && t.App03TaskUsers.some(user => user.UserID === userContext.UserId))?.length ?? 0;
                        //const totalCriticalTasks: number = tasksForCounters?.filter(t => t.Workflow?.App03PortfolioID === p.ID && t.TaskPriorityID === TaskPriority.Critical)?.length ?? 0;
                        //const totalUserCriticalTasks: number = tasksForCounters?.filter(t => t.Workflow?.App03PortfolioID === p.ID && t.TaskPriorityID === TaskPriority.Critical && t.App03TaskUsers.some(user => user.UserID === userContext.UserId))?.length ?? 0;

                        return {
                            key: p.ID,
                            Name: p.Title,
                            Portfolio: p.Portfolio?.Title ?? '',
                            Workflows: totalWorkflows,
                            App03ProjectType: p.App03ProjectTypeID === PortfolioTypes.General ? "General" : "Project",
                            Owner: p.OwnerUser?.Title ?? '',
                        };
                    })}
                    entityForm={(showForm, entityId, onSaved, onCancelled, entityDescription1, entityDescription1DirectorateID, selectedEntity) => {
                        if (entityId > 0 && selectedEntity?.App03ProjectTypeID === PortfolioTypes.Project) {
                            return (
                                <MessageDialog
                                    hidden={!showForm}
                                    title={`${selectedEntity?.Title} cannot be edited`}
                                    handleOk={onCancelled}
                                >
                                    <>
                                        <p>
                                            This project was created in the <strong>{projectPortfoliosAppName}</strong> app.
                                            To make changes, please open the <strong>{projectPortfoliosAppName}</strong> app
                                            and edit it from there.
                                        </p>

                                    </>

                                </MessageDialog>
                            );
                        } else {
                            return (
                                <WFProjectForm
                                    {...props}
                                    showForm={showForm}
                                    entityId={entityId}
                                    onSaved={onSaved}
                                    frameworkID={props.frameworkID}
                                    onCancelled={onCancelled}
                                    showEntityDescription1={portfolioIdForProjects !== undefined ? true : false}
                                    entityDescription1={entityDescription1}
                                    defaultValues={[{ field: 'PortfolioID', value: Number(portfolioIdForProjects) }]}
                                />
                            );
                        }
                    }}
                    onCheckDeleteCustomMsg={(entityId, selectedEntity) => {
                        if (entityId > 0 && selectedEntity?.App03ProjectTypeID === PortfolioTypes.Project) {
                            return (
                                <>
                                    <p>
                                        This project was created in the <strong>{projectPortfoliosAppName}</strong> app.
                                        To delete it, please open the <strong>{projectPortfoliosAppName}</strong> app
                                        and delete it from there.
                                    </p>

                                </>
                            );
                        } else {
                            return null;
                        }
                    }}
                    onCheckDelete={id => dataServices.app03ProjectService.entityChildren(id)}
                    onDelete={id => dataServices.app03ProjectService.delete(id)}
                />
            }

            {entity === 'Epics' &&
                <EntityList<IApp03Epic>
                    {...props}
                    ref={epicsEntityListRef}
                    //defaultView='list'
                    isMobile={isMobile}
                    disableAdd={false}
                    enableShowHideClosedEntities={false}
                    entityName={{ Plural: 'Epics', Singular: 'Epic' }}
                    //barColorBeforeTitle='#40A182'
                    veryFirstItems={(ws) => getPreFarItems(['Workflows'])}
                    showEntityDescription1={true}
                    loadEntityDescription1={() => {
                        return dataServices.app03WorkflowService.readTitleForWorkflow('Epics', 'Epic', Number(workflowIdForEpics));
                    }}

                    columns={[

                        {
                            key: '1',
                            name: 'Epic',
                            fieldName: 'Name',
                            ...workflowNameWidths,
                            isResizable: true,
                            onRender: item =>
                                renderAsLink(item.key, item.Name, (ID: number) => epicsEntityListRef.current?.openEditForm(ID))
                        },
                        { key: '2', name: 'Status', fieldName: 'EpicStatus', minWidth: 70, maxWidth: 80, isCollapsible: true, isResizable: true },
                        { key: '3', name: 'Priority', fieldName: 'EpicPriority', minWidth: 40, maxWidth: 60, isResizable: true, onRender: item => renderTaskPriority(item.EpicPriority, item.EpicPriorityColor) },
                        { key: '4', name: 'Dates', minWidth: 100, maxWidth: 120, isResizable: true, isCollapsible: true, onRender: item => renderDateRange(item.StartDate, item.EndDate) },
                        { key: '5', name: 'Owner', fieldName: 'EpicOwner', minWidth: 100, maxWidth: 120, isResizable: true, isCollapsible: true },
                        // { key: '4', name: 'Dates', minWidth: 100, maxWidth: 120, isResizable: true, isCollapsible: true, onRender: item => renderDateRange(item.StartDate, item.EndDate) },
                        // { key: '5', name: 'Velocity Target', fieldName: 'VelocityTarget', minWidth: 70, maxWidth: 95, isCollapsible: true, isResizable: true },
                        // { key: '6', name: 'Actual Velocity', fieldName: 'ActualVelocity', minWidth: 70, maxWidth: 90, isCollapsible: true, isResizable: true },
                    ]}

                    loadListItems={async showClosedEntities => {
                        return dataServices.app03EpicService.readAllForList(props.frameworkID, Number(workflowIdForEpics));
                    }}
                    mapEntitiesToListItems={epics => epics.map(epic => {
                        return {
                            key: epic.ID,
                            Name: epic.Title,
                            EpicStatus: getApp03EpicStatusDisplay(epic.EpicStatus),
                            EpicPriority: getTaskPriorityString(epic.EpicPriorityID),
                            EpicPriorityColor: TaskPriorityColors[epic.EpicPriorityID],
                            StartDate: epic.StartDate,
                            StartDateValue: epic.StartDate?.valueOf(),
                            EndDate: epic.TargetEndDate,
                            EndDateValue: epic.TargetEndDate?.valueOf(),
                            EpicOwner: epic.EpicOwner?.Title ?? '',
                        };
                    })}
                    ganttTopOffset={145}
                    showListAndGantt={true}
                    enableGanttDrag={true}
                    renderGanttTooltip={renderGanttTooltip_epic}
                    buildGanttData={buildEpicGanttData}

                    // buildGanttData={(epics) => {
                    //     console.log("Building Gantt data for epics");

                    //     const ganttTasks: any[] = [];
                    //     const ganttLinks: any[] = [];

                    //     /* ===========================
                    //        ADD EPIC TASKS
                    //     =========================== */

                    //     epics.forEach(e => {

                    //         if (!e.StartDate || !e.TargetEndDate) return;

                    //         const start = new Date(e.StartDate);
                    //         const end = new Date(e.TargetEndDate);

                    //         ganttTasks.push({
                    //             id: `epic-${e.ID}`,
                    //             text: e.Title,
                    //             start,
                    //             end,
                    //             type: "epic",
                    //             _entity: e
                    //         });
                    //     });

                    //     /* ===========================
                    //        BUILD DEPENDENCY LINKS
                    //     =========================== */

                    //     let linkId = 1;

                    //     epics.forEach(e => {

                    //         if (e.Dependencies && e.Dependencies.length > 0) {

                    //             e.Dependencies.forEach((dep: any) => {

                    //                 ganttLinks.push({
                    //                     id: linkId++,
                    //                     source: `epic-${dep.DependentEpicID}`,
                    //                     target: `epic-${e.ID}`,
                    //                     type: "e2s"
                    //                 });

                    //             });
                    //         }
                    //     });

                    //     return { tasks: ganttTasks, links: ganttLinks };
                    // }}


                    ganttColumns={app03EpicColumns}
                    ganttTaskTypes={app03EpicTypes}
                    ganttZoom={svarGanttZoomConfig}
                    ganttHighlightTime={ganttHighlightTime}
                    defaultGanttZoomLevel={GanttZoomLevel.MonthWeek}

                    onUpdateDates={async (entityId: number, startDate: Date, endDate: Date) => {
                        const payload: IUpdateEntityDatesPayload = {
                            EntityID: entityId,
                            StartDate: startDate,
                            EndDate: endDate
                        };

                        await dataServices.app03EpicService.updateEntityDates(payload);
                    }}

                    getTaskId={(epic) => `epic-${epic.ID}`}

                    getEntityIdFromTask={(task) => {
                        if (task.type === "epic" && task._entity) {
                            return task._entity.ID;
                        }
                        return null;
                    }}

                    isTaskDraggable={(task) => task.type === "epic"}
                    isTaskResizable={(task) => task.type === "epic"}

                    clearSelectionOnNonEntityTask={true}

                    startDateField="StartDate"
                    endDateField="TargetEndDate"

                    editableTaskTypes={["epic"]}


                    formTypePage={true}
                    entityForm={(showForm, entityId, onSaved, onCancelled) =>
                        <EpicFormPage
                            showForm={showForm}
                            entityId={entityId}
                            frameworkID={props.frameworkID}
                            workflowID={Number(workflowIdForEpics)}
                            defaultValues={[
                                { field: 'WorkflowID', value: Number(workflowIdForEpics) },
                                { field: 'FrameworkID', value: props.frameworkID }
                            ]}
                            onSaved={onSaved}
                            onCancelled={onCancelled}
                            {...props}
                        />
                    }

                    onCheckDelete={id => dataServices.app03EpicService.entityChildren(id)}
                    onDelete={id => dataServices.app03EpicService.delete(id)}
                />
            }

            {entity === 'Sprints' &&
                <EntityList<IApp03Sprint>
                    {...props}
                    ref={sprintsEntityListRef}
                    disableAdd={false}
                    enableShowHideClosedEntities={false}
                    entityName={{ Plural: 'Sprints', Singular: 'Sprint' }}
                    //barColorBeforeTitle='#40A182'
                    veryFirstItems={(ws) => getPreFarItems(['Workflows'])}
                    additionalItemsOnSelection={(s) => {
                        return [
                            {
                                key: 'CloseSprint',
                                name: 'Close Sprint',
                                iconProps: { iconName: 'Flag' },
                                disabled: s?.SprintStatus !== App03SprintStatus.Active, // disable if not active
                                onClick(ev, item) {
                                    if (s?.SprintStatus === App03SprintStatus.Active) {
                                        //console.log('Closing sprint:', s);
                                        sprintsEntityListRef.current?.openForm2(s?.ID);
                                    }
                                },
                            },
                        ];
                    }}

                    showEntityDescription1={true}
                    loadEntityDescription1={() => {
                        return dataServices.app03WorkflowService.readTitleForWorkflow('Sprints', 'Sprint', Number(workflowIdForSprints));
                    }}

                    columns={[
                        { key: '1', name: 'No', fieldName: 'SprintNumber', minWidth: 20, maxWidth: 30, isResizable: true },
                        {
                            key: '2',
                            name: 'Sprint',
                            fieldName: 'Name',
                            ...workflowNameWidths,
                            isResizable: true,
                            onRender: item =>
                                renderAsLink(item.key, item.Name, (ID: number) => sprintsEntityListRef.current?.openEditForm(ID))
                        },
                        { key: '3', name: 'Status', fieldName: 'SprintStatus', minWidth: 70, maxWidth: 80, isCollapsible: true, isResizable: true },
                        { key: '4', name: 'Dates', minWidth: 100, maxWidth: 120, isResizable: true, isCollapsible: true, onRender: item => renderDateRange(item.StartDate, item.EndDate) },
                        { key: '5', name: 'Velocity Target', fieldName: 'VelocityTarget', minWidth: 70, maxWidth: 95, isCollapsible: true, isResizable: true },
                        { key: '6', name: 'Actual Velocity', fieldName: 'ActualVelocity', minWidth: 70, maxWidth: 90, isCollapsible: true, isResizable: true },
                    ]}

                    loadListItems={async showClosedEntities => {
                        return dataServices.app03SprintService.readAllForList(Number(workflowIdForSprints));
                    }}
                    mapEntitiesToListItems={sprints => sprints.map(s => {
                        return {
                            key: s.ID,
                            SprintNumber: s.SprintNumber,
                            Name: s.Title,
                            SprintStatus: s.SprintStatus,
                            StartDate: s.StartDate,
                            StartDateValue: s.StartDate?.valueOf(),
                            EndDate: s.EndDate,
                            EndDateValue: s.EndDate?.valueOf(),
                            VelocityTarget: s.VelocityTarget,
                            ActualVelocity: s.ActualVelocity,
                        };
                    })}
                    formTypePage={true}
                    entityForm={(showForm, entityId, onSaved, onCancelled) =>
                        <SprintFormPage
                            frameworkID={props.frameworkID}
                            showForm={showForm}
                            entityId={entityId}
                            defaultValues={[{ field: 'WorkflowID', value: Number(workflowIdForSprints) }]}
                            onSaved={onSaved}
                            onCancelled={onCancelled}
                            {...props}
                        />
                    }
                    form2={(showForm, entityId, onSaved, onCancelled, selectedEntity) =>
                        <CloseSprintForm
                            showForm={showForm}
                            sprintID={entityId}
                            sprint={selectedEntity}
                            onSaved={onSaved}
                            onClosed={onCancelled}
                            {...props}
                        />
                    }
                    onCheckDelete={id => dataServices.app03SprintService.entityChildren(id)}
                    onDelete={id => dataServices.app03SprintService.delete(id)}
                />
            }

            {entity === 'Workflows' &&
                <EntityList<IApp03Workflow>
                    {...props}
                    disableAdd={false}
                    //enableShowHideClosedEntities={true}
                    entityName={{ Plural: 'Workstreams', Singular: 'Workstream' }}
                    //entity={portfolioIdForWorkflows !== undefined ? "Workstreams for portfolio" : "Workstreams"} //just to reload
                    entity={portfolioIdForWorkflows !== undefined ? "Workstreams for portfolio" : projectIdForWorkflows !== undefined ? "Workstreams for project" : "Workstreams"} //just to reload
                    showEntityDescription1={
                        portfolioIdForWorkflows !== undefined
                            ? true
                            : projectIdForWorkflows !== undefined
                                ? true
                                : false
                    }

                    loadEntityDescription1={() => {
                        return portfolioIdForWorkflows !== undefined
                            ? dataServices.app03PortfolioService.readTitleForPortfolio(
                                'Workstreams',
                                'Workstream',
                                Number(portfolioIdForWorkflows)
                            )
                            : projectIdForWorkflows !== undefined
                                ? dataServices.app03ProjectService.readTitleForProject(
                                    'Workstreams',
                                    'Workstream',
                                    Number(projectIdForWorkflows)
                                )
                                : null;
                    }}

                    onEntityDescriptionChange={props.onEntityDescriptionChange}
                    veryFirstItems={(p) =>
                        portfolioIdForWorkflows !== undefined
                            ? getPreFarItems(['Portfolios'])
                            : projectIdForWorkflows !== undefined
                                ? getPreFarItems(['Projects'])
                                : null
                    }

                    //barColorBeforeTitle='#40A182'
                    additionalItemsOnSelection={(wf) => {
                        return ([
                            {
                                key: 'Access',
                                name: 'Access',
                                iconProps: { iconName: 'FabricUserFolder' },
                                onClick(ev, item) {
                                    history.push(`${getAppUrl()}/workflow-users/${encodeRouteId(wf?.ID!)}`);
                                },
                            },
                            {
                                key: 'Epics',
                                name: 'Epics',
                                iconProps: { iconName: 'MiniExpand' },
                                onClick(ev, item) {
                                    openEpics(wf?.ID);
                                },
                            },
                            ...(props.frameworkID === App03Frameworks.Scrum
                                ? [{
                                    key: 'Sprints',
                                    name: 'Sprints',
                                    iconProps: { iconName: 'MiniExpand' },
                                    onClick(ev, item) {
                                        openSprints(wf?.ID);
                                    },
                                }]
                                : []),
                            {
                                key: 'Tasks',
                                name: 'Work Items',
                                iconProps: { iconName: 'MiniExpand' },
                                onClick(ev, item) {
                                    openTasks(wf?.ID);
                                },
                            },
                        ]);
                    }}
                    columns={[
                        { key: '0', name: '', minWidth: 28, maxWidth: 28, isResizable: true, onRender: item => renderWorkflowIcon(item.WorkflowTypeID) },
                        { key: '1', name: 'Name', fieldName: 'Name', isMultiline: true, ...workflowNameWidths, isResizable: true, onRender: item => renderAsLink(item.key, item.Name, (ID: number) => openTasks(ID)) },
                        ...(portfolioIdForWorkflows === undefined && projectIdForWorkflows === undefined
                            ? [{ key: '2', name: 'Portfolio', fieldName: 'Portfolio', ...workflowNameWidths, isResizable: true, isMultiline: true }]
                            : []),

                        { key: '3', name: 'Active Epics', fieldName: 'ActiveEpics', minWidth: 75, maxWidth: 80, isCollapsible: true, isResizable: true },

                        ...(props.frameworkID === App03Frameworks.Scrum
                            ? [{ key: '4', name: 'Active Tasks', fieldName: 'ActiveTasks', minWidth: 80, maxWidth: 100, isCollapsible: true, isResizable: true }]
                            : [{ key: '4', name: 'Active Tasks', fieldName: 'ActiveTasks', minWidth: 80, maxWidth: 100, isCollapsible: true, isResizable: true, onRender: item => renderActiveTasks(item.ActiveTasks, item.CriticalTasks) },]),

                        //{ key: '3', name: 'Active Tasks', fieldName: 'ActiveTasks', minWidth: 60, maxWidth: 90, isCollapsible: true, isResizable: true, onRender: item => renderActiveTasks(item.ActiveTasks, item.CriticalTasks) },
                        //{ key: '3', name: 'Active Tasks', fieldName: 'ActiveTasks', minWidth: 80, maxWidth: 100, isCollapsible: true, isResizable: true },
                        //{ key: '4', name: 'Critical', fieldName: 'CriticalTasks', minWidth: 60, maxWidth: 60, isCollapsible: true, isResizable: true, onRender: item => renderWithBackgroundColor(item.CriticalTasks, 'red', 'white') },
                        { key: '5', name: 'Type', fieldName: 'Type', minWidth: 80, maxWidth: 100, isCollapsible: true, isResizable: true },
                        { key: '6', name: 'Owners', fieldName: 'Administrators', minWidth: 150, maxWidth: 200, isResizable: true, isCollapsible: true, isMultiline: true },
                    ]}
                    loadListItems={async showClosedEntities => {
                        if (projectIdForWorkflows !== undefined || portfolioIdForWorkflows !== undefined) {
                        }
                        else {
                            props.setLastProjectIdForChilds(null);
                            props.setLastPortfolioIdForChilds(null);
                        }

                        // if (portfolioIdForWorkflows !== undefined) {
                        // }
                        // else {
                        //     props.setLastPortfolioIdForChilds(null);
                        // }

                        const tasks: IApp03Task[] = await dataServices.app03TaskService.readAllActiveTasksForCounters(props.frameworkID);
                        setTasksForCounters(tasks);

                        const epics: IApp03Epic[] = await dataServices.app03EpicService.readAllActiveEpicsForCounters(props.frameworkID);
                        setEpicsForCounters(epics);

                        const projects: IProject[] = await dataServices.projectService.readAllForLookup();

                        const workflows = portfolioIdForWorkflows !== undefined
                            ? await dataServices.app03WorkflowService.readAllForList(
                                showClosedEntities,
                                props.frameworkID,
                                userContext.UserId,
                                Number(portfolioIdForWorkflows)
                            )
                            : projectIdForWorkflows !== undefined
                                ? await dataServices.app03WorkflowService.readAllForList(
                                    showClosedEntities,
                                    props.frameworkID,
                                    userContext.UserId,
                                    undefined,
                                    Number(projectIdForWorkflows)
                                )
                                : await dataServices.app03WorkflowService.readAllForList(
                                    showClosedEntities,
                                    props.frameworkID,
                                    userContext.UserId
                                );



                        // create a map for quick lookup
                        const projectMap = new Map(projects.map(p => [p.ID, p.Title]));

                        // enrich workflows with project title
                        const workflowsWithProject = workflows.map(wf => ({
                            ...wf,
                            ProjectTitle: wf.PPProjectID ? projectMap.get(wf.PPProjectID) ?? null : null
                        }));

                        return workflowsWithProject;
                    }}

                    mapEntitiesToListItems={workflows => workflows.map(wf => {

                        //console.log(wf);
                        const currentUser = wf.App03WorkflowUsers?.find(
                            u => u.UserID === userContext.UserId
                        );

                        const isViewOnly = currentUser?.ViewOnly ?? false;

                        const totalEpics: number = epicsForCounters?.filter(t => t.WorkflowID === wf.ID)?.length ?? 0;
                        const totalTasks: number = tasksForCounters?.filter(t => t.WorkflowID === wf.ID)?.length ?? 0;
                        const totalUserTasks: number = tasksForCounters?.filter(t => t.WorkflowID === wf.ID && t.App03TaskUsers.some(user => user.UserID === userContext.UserId))?.length ?? 0;
                        const totalCriticalTasks: number = tasksForCounters?.filter(t => t.WorkflowID === wf.ID && t.TaskPriorityID === TaskPriority.Critical)?.length ?? 0;
                        const totalUserCriticalTasks: number = tasksForCounters?.filter(t => t.WorkflowID === wf.ID && t.TaskPriorityID === TaskPriority.Critical && t.App03TaskUsers.some(user => user.UserID === userContext.UserId))?.length ?? 0;

                        let portfolio = '';
                        if (wf.FrameworkID === App03Frameworks.Kanban && wf.PPProjectID > 0) {
                            portfolio = `${wf.App03Portfolio?.Title} (${wf['ProjectTitle']})`;
                        }
                        else if (wf.FrameworkID === App03Frameworks.Scrum) {
                            if (wf.ParentTypeID === App03WorkflowParentTypes.Portfolio) {
                                portfolio = wf.App03Portfolio?.Title;
                            }
                            else {
                                portfolio = `${wf?.App03Project?.Portfolio?.Title} (${wf?.App03Project?.Title})`;
                            }
                        }
                        else {
                            portfolio = wf.App03Portfolio?.Title;
                        }

                        return {
                            key: wf.ID,
                            WorkflowTypeID: wf.WorkflowTypeID,
                            IsViewOnly: isViewOnly,
                            Name: wf.Title,
                            Portfolio: portfolio,
                            ActiveEpics: totalEpics,
                            ActiveTasks: `${totalUserTasks}/${totalTasks}`, // '2/10',
                            CriticalTasks: `${totalUserCriticalTasks}/${totalCriticalTasks}`,
                            Type: getWorkflowTypeString(wf.WorkflowTypeID),
                            Administrators: [
                                wf.App03WorkflowUsers && wf.App03WorkflowUsers.some(a => a.User != null && a.IsAdmin)
                                    ? wf.App03WorkflowUsers.filter(pc => pc.IsAdmin).map(pc => pc.User?.Title).join(', ')
                                    : null,
                            ].filter(Boolean).join(', '),
                        };
                    })}


                    onCheckDeleteCustomMsg={(entityId, selectedEntity) => {
                        if (entityId > 0 && selectedEntity?.WorkflowTypeID === WorkflowType.ProjectWS) {
                            return (
                                <>
                                    <p>
                                        This workstream was created in the <strong>{projectPortfoliosAppName}</strong> app.
                                        To delete it, please open the <strong>{projectPortfoliosAppName}</strong> app
                                        and delete it from there.
                                    </p>

                                </>
                            );
                        } else {
                            return null;
                        }
                    }}
                    entityForm={(showForm, entityId, onSaved, onCancelled, entityDescription1) =>
                        <WorkflowForm
                            {...props}
                            frameworkID={props.frameworkID}
                            showForm={showForm}
                            entityId={entityId}
                            onSaved={onSaved}
                            onCancelled={onCancelled}
                            showEntityDescription1={
                                portfolioIdForWorkflows !== undefined
                                    ? true
                                    : projectIdForWorkflows !== undefined
                                        ? true
                                        : false
                            }

                            entityDescription1={entityDescription1}
                            defaultValues={
                                portfolioIdForWorkflows !== undefined
                                    ? [{ field: 'ParentTypeID', value: App03WorkflowParentTypes.Portfolio }, { field: 'App03PortfolioID', value: Number(portfolioIdForWorkflows) }]
                                    : projectIdForWorkflows !== undefined
                                        ? [{ field: 'ParentTypeID', value: App03WorkflowParentTypes.Project }, { field: 'App03ProjectID', value: Number(projectIdForWorkflows) }]
                                        : []
                            }

                        />
                    }
                    viewForm={(showForm, entityId, onClosed, entityDescription1) =>
                        <WorkflowView
                            {...props}
                            showForm={showForm}
                            entityId={entityId}
                            entityName='Workflow'
                            loadEntity={workflowID => dataServices.app03WorkflowService.readForView(workflowID)}
                            onClosed={onClosed}
                        />
                    }
                    onCheckDelete={id => dataServices.app03WorkflowService.entityChildren(id)}
                    onDelete={id => dataServices.app03WorkflowService.delete(id)}
                />
            }
            {entity === 'WorkflowUsers' &&
                <EntityList
                    {...props}
                    entityName={{ Plural: 'Workflow Users', Singular: 'Workflow User' }}
                    columns={[
                        { key: '1', name: 'User', fieldName: 'User', minWidth: 200, maxWidth: 300, isResizable: true },
                        { key: '2', name: 'Owner', fieldName: 'IsAdmin', minWidth: 80, maxWidth: 100, isResizable: true, isCollapsible: true },
                        { key: '3', name: 'View Only', fieldName: 'ViewOnly', minWidth: 80, isResizable: true, isCollapsible: true }
                    ]}
                    veryFirstItems={(ws) => getPreFarItems(['Workflows'])}
                    loadListItems={() => dataServices.app03WorkflowUserService.readAllForList(Number(workflowIdForWorkflowUsers))}
                    showEntityDescription1={true}
                    loadEntityDescription1={() => {
                        return dataServices.app03WorkflowService.readTitleForWorkflow('Users', 'User', Number(workflowIdForWorkflowUsers));
                    }}
                    mapEntitiesToListItems={users => users.map(uw => (
                        {
                            key: uw.ID,
                            User: uw.User?.Title,
                            Workflow: uw.App03Workflow?.Title,
                            IsAdmin: uw.IsAdmin ? 'Yes' : 'No',
                            ViewOnly: uw.ViewOnly ? 'Yes' : 'No',
                        }
                    ))}
                    entityForm={(showForm, entityId, onSaved, onCancelled, entityDescription1) =>
                        <UserWorkflowForm
                            {...props}
                            showForm={showForm}
                            entityId={entityId}
                            showEntityDescription1={true}
                            entityDescription1={entityDescription1}
                            defaultValues={[{ field: 'App03WorkflowID', value: Number(workflowIdForWorkflowUsers) }]}
                            onSaved={onSaved}
                            onCancelled={onCancelled} />
                    }
                    onCheckDelete={() => Promise.resolve([])}
                    onDelete={id => dataServices.app03WorkflowUserService.delete(id)}
                />
            }

            {entity === 'App03Templates' &&
                <EntityList<IApp03Template>
                    {...props}
                    disableAdd={false}
                    enableShowHideClosedEntities={false}
                    entityName={{ Plural: 'Templates', Singular: 'Template' }}
                    additionalItemsOnSelection={(t) => {
                        fetchChildCountsForSelectedTemplate(t?.ID);
                        return ([
                            {
                                key: 'WorkItemTypes',
                                name: workItemTypesLabelForSelectedTemplate,
                                iconProps: { iconName: 'MiniExpand' },
                                onClick(ev, item) {
                                    openWorkItemTypesForTemplate(t?.ID);
                                },
                            },
                        ]);
                    }}
                    columns={[
                        { key: '1', name: 'Template', fieldName: 'Name', ...workflowNameWidths, isResizable: true, isMultiline: true, },
                        { key: '2', name: 'Description', fieldName: 'Description', ...workflowNameWidths, isResizable: true, isMultiline: true, },
                        // { key: '3', name: 'Workstreams', fieldName: 'Workflows', minWidth: 80, maxWidth: 100, isCollapsible: true, isResizable: true },
                    ]}

                    loadListItems={async showClosedEntities => {
                        //const workflows: IApp03Workflow[] = await dataServices.app03WorkflowService.readAllWorkflowsForCounters(showClosedEntities);
                        //setWorkflowsForCounters(workflows);

                        return dataServices.app03TemplateService.readAllForList(props.frameworkID);
                    }}
                    mapEntitiesToListItems={templates => templates.map(template => {
                        //const totalWorkflows: number = workflowsForCounters?.filter(wf => wf.App03PortfolioID === p.ID)?.length ?? 0;

                        return {
                            key: template.ID,
                            Name: template.Title,
                            Description: template.Description,
                        };
                    })}
                    entityForm={(showForm, entityId, onSaved, onCancelled, entityDescription1) =>
                        <App03TemplateForm
                            {...props}
                            frameworkID={props.frameworkID}
                            showForm={showForm}
                            entityId={entityId}
                            onSaved={onSaved}
                            onCancelled={onCancelled} />
                    }
                    onCheckDelete={id => dataServices.app03TemplateService.entityChildren(id)}
                    onDelete={id => dataServices.app03TemplateService.delete(id)}
                />
            }

            {entity === 'WorkItemTypes' &&
                <EntityList
                    {...props}
                    entityName={{ Plural: 'Work Item Types', Singular: 'Work Item Type' }}
                    columns={[
                        { key: '1', name: 'Work Item Type', fieldName: 'Title', ...workflowNameWidths, isResizable: true, isMultiline: true, },
                        { key: '2', name: 'System Type', fieldName: 'SystemType', minWidth: 80, maxWidth: 120, isResizable: true, isCollapsible: true },
                        { key: '3', name: 'Default', fieldName: 'IsDefault', minWidth: 50, maxWidth: 50, isResizable: true, },
                    ]}
                    veryFirstItems={(wits) => getPreFarItems(['Templates'])}
                    loadListItems={() => dataServices.app03WorkItemTypeService.readAllForList(Number(templateIDForWorkItemTypes))}
                    showEntityDescription1={true}
                    loadEntityDescription1={() => {
                        return dataServices.app03TemplateService.readTitleForTemplate('Work Item Types', 'Work Item Type', Number(templateIDForWorkItemTypes));
                    }}
                    mapEntitiesToListItems={wits => wits.map(wit => (
                        {
                            key: wit.ID,
                            Title: wit.Title,
                            IsDefault: wit.IsDefault === true ? 'Yes' : '',
                            SystemType: wit.SystemType,
                        }
                    ))}
                    entityForm={(showForm, entityId, onSaved, onCancelled, entityDescription1) =>
                        <App03WorkItemTypeForm
                            {...props}
                            showForm={showForm}
                            entityId={entityId}
                            showEntityDescription1={true}
                            entityDescription1={entityDescription1}
                            defaultValues={[{ field: 'App03TemplateID', value: Number(templateIDForWorkItemTypes) }]}
                            onSaved={onSaved}
                            onCancelled={onCancelled} />
                    }
                    onCheckDelete={id => dataServices.app03WorkItemTypeService.entityChildren(id)}
                    onDelete={id => dataServices.app03WorkItemTypeService.delete(id)}
                />
            }

        </ErrorBoundary>
    );

};

export default withErrorHandling(App03WorkflowsAdmin);